import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { logger } from '@fiverr-private/obs';
import pathfinder from '@fiverr-private/pathfinder';
import { axiosWrapper, ReviewsData, useSellerData, FederatedSeller } from '@fiverr-private/seller_pages_toolbox';
import { useSelectedRole } from './useSelectedRole';
import { useSellerReviews, useSellerReviewsUpdater } from './useSellerReviews';
import { useSelectedRoleId } from './useSelectedRoleId';

const TIMEOUT = 1200;
const CACHE_STALE_TIME = 60 * 1000;

const fetchReviews = async (username: string, selectedRoleId: string | null) => {
    const params = {
        refresh_role: true,
        role_id: selectedRoleId,
        username,
    };

    const { data } = await axiosWrapper.get<ReviewsData>(
        pathfinder('seller_page_reviews'),
        {
            timeout: TIMEOUT,
            params,
        },
        'reviews_kimera'
    );

    return data;
};

export const useFetchReviewsOnRoleSwitch = () => {
    const { username } = useSellerData(selectSellerSlice);
    const reviews = useSellerReviews();
    const updateReviews = useSellerReviewsUpdater();
    const selectedRoleId = useSelectedRoleId();
    const [initialSelectedRoleId] = useState(selectedRoleId);
    const selectedRole = useSelectedRole();
    const currentRoleRatingCount = selectedRole?.rating?.count;

    const isOnRoleTabWithoutReviews = selectedRoleId && !currentRoleRatingCount;
    const initialFetch = initialSelectedRoleId === selectedRoleId;
    const shouldFetchReviews = !isOnRoleTabWithoutReviews && !initialFetch;

    const { data, isFetching } = useQuery(
        ['reviews', username, selectedRoleId],
        () => fetchReviews(username, selectedRoleId),
        {
            enabled: shouldFetchReviews,
            initialData: initialFetch ? reviews : undefined,
            refetchOnWindowFocus: false,
            staleTime: CACHE_STALE_TIME,
            onError: (error) => {
                const err = new Error('Failed to fetch role seller reviews from Reviews Kimera');
                logger.error(err, {
                    params: { role_id: selectedRoleId, username },
                    context: 'reviews',
                    cause: error,
                });
            },
        }
    );

    useEffect(() => {
        const reviews = isOnRoleTabWithoutReviews ? undefined : data;

        updateReviews(reviews);
    }, [updateReviews, isOnRoleTabWithoutReviews, initialFetch, data]);

    return { areReviewsLoading: isFetching };
};

function selectSellerSlice(seller: FederatedSeller) {
    return {
        username: seller.user.name,
    };
}
